<template>
    <div class="p-fluid custom-password flex align-items-center gap-2 relative field"
         :style="'width: ' + width + 'px;'">
        <CustomLabel :name="name" :label="label" :required="required"/>
        <span class="p-float-label p-input-icon-left">
            <i :class="icon" class="icon" />
            <Password :id="name" :name="name" v-model="internalValue" :feedback="feedback" :toggleMask="true"
                      @input="handleChange" @blur="handleBlur"
                      v-tooltip.top="title" :placeholder="placeholder"
                      :autocomplete="autocomplete" :aria-label="name">
                <template #content>
                    <!-- This overrides default content-->
                    <div/>
                </template>
                <template #footer v-if="lowercase || uppercase || numeric
                    || charactersNumber || specialCharacter">
                    <p class="p-mt-2">Requirements</p>
                    <ul class="p-pl-2 p-ml-2 p-mt-0" style="line-height: 1.5">
                        <li v-if="lowercase">At least one lowercase letter</li>
                        <li v-if="uppercase">At least one uppercase letter</li>
                        <li v-if="numeric">At least one digit</li>
                        <li v-if="specialCharacter">At least one special character</li>
                        <li v-if="charactersNumber">At least 8 characters</li>
                    </ul>
                </template>
                <template #footer v-else>
                    <p class="p-mt-2">Password meets the requirements</p>
                </template>
            </Password>
        </span>
        <CustomValidationMessage :errorMessage="errorMessage" />
    </div>
</template>

<script>
    import Password from "primevue/password";
    import {useField} from "vee-validate";
    import Tooltip from "primevue/tooltip";
    import CustomLabel from "./inner/CustomLabel";
    import CustomValidationMessage from "./inner/CustomValidationMessage";

    export default {
        name: "CustomPassword",

        props: {
            name: {
                type: String,
            },
            feedback: {
                type: Boolean,
                default: false,
            },
            label: {
                type: String,
            },
            icon: {
                type: String,
                default: "pi pi-lock",
            },
            title: {
                type: String,
                default: null,
            },
            width: {
                type: String,
            },
            autocomplete: {
                type: String,
                default: undefined,
            },
            required: {
                type: Boolean,
                default: false,
            },
            rules: undefined,
            modelValue: null,
            placeholder: "",
            customErrorMessage: {
                type: String,
                default: "",
            },
        },

        emits: ["update:modelValue"],

        directives: {
            tooltip: Tooltip,
        },

        data() {
            return {
                lowercase: true,
                uppercase: true,
                numeric: true,
                charactersNumber: true,
                specialCharacter: true,
            };
        },

        watch: {
            modelValue() {
                this.inputValue = this.modelValue;
            },
        },

        computed: {
            internalValue: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.specialCharacter = !(/(?=.*[!@#$%^&*()[\]{};:+=|,<.>`~£§?/'\\-_"])/.test(value));
                    this.uppercase = !(/(?=.*[A-ZŻŹĆŃÓŁĘĄŚ])/.test(value));
                    this.lowercase = !(/(?=.*[a-zżźćńółęąś])/.test(value));
                    this.numeric = !(/(?=.*\d)/.test(value));
                    this.charactersNumber = !(/(?=.*[a-zA-ZżźćńółęąśZŻŹĆŃÓŁĘĄŚ]).{8,}$/.test(value));
                    this.$emit("update:modelValue", value);
                },
            },
        },

        setup(props) {
            const {
                value: inputValue,
                errorMessage,
                handleBlur,
                handleChange,
                meta,
            } = useField(props.name, props.rules, {
                initialValue: props.modelValue,
            });

            return {
                handleChange,
                handleBlur,
                errorMessage,
                inputValue,
                meta,
            };
        },

        components: {Password, CustomLabel, CustomValidationMessage},
    };
</script>

<style lang="scss">
    .custom-password {
        .p-icon {
            right: 1rem;
        }
    }
</style>
